/**
 * CUSTOM:finnstyle-more-for-you-v1.0 — entire file is this customization.
 * See customizations/0023-more-for-you-tabs.md.
 *
 * Fills the tabs of sections/more-for-you.liquid. Each source is the same section
 * rendered by another endpoint; the matching .tabs__panel is taken from the response.
 * Panels go into the tab's <template> unless they are on screen, so product-tabs.js
 * clones them when the tab opens and the slider starts on a visible element.
 * Empty tabs are hidden (not removed: product-tabs.js indexes its buttons by position).
 */
if (!customElements.get('more-for-you')) {
  customElements.define(
    'more-for-you',
    class MoreForYou extends HTMLElement {
      constructor() {
        super();

        this.productId = parseInt(this.dataset.productId);
        this.productsToShow = parseInt(this.dataset.productsToShow);
        this.tabs = this.querySelector('[is="product-tabs"]');
        this.panels = this.querySelector('.tabs__panels');
        this.nav = this.querySelector('.tabs-nav');
        this.activeIndex = 0;

        this.addEventListener('tabChange', (event) => this.revealActiveTab(event.detail.selectedIndex));

        // Nothing viewed yet: hide the tab before it can flash.
        this.recentIds = this.getRecentIds();
        if (!this.recentIds.length) this.hideTab('recently_viewed');

        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      init() {
        this.syncActiveTab();

        if (this.getButton('recommendations')) {
          this.load('recommendations', this.dataset.recommendationsUrl);
        }

        if (this.getButton('recently_viewed') && this.recentIds.length) {
          const query = this.recentIds.map((id) => 'id:' + id).join(' OR ');
          this.load('recently_viewed', this.dataset.searchUrl + encodeURIComponent(query));
        }

        const routeHandle = this.getRouteCollection();
        if (this.getButton('collection') && routeHandle && routeHandle !== this.dataset.collection) {
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          this.load('collection', `${root}collections/${routeHandle}?section_id=${this.dataset.sectionId}`);
        }
      }

      load(source, url) {
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const panel = html.querySelector(`.tabs__panel[data-mfy-source="${source}"]`);

            if (panel && source === 'collection') this.trimCollection(panel);

            if (!panel || !panel.querySelector('[data-mfy-product]')) {
              // A route collection with nothing else in it: keep the server's pick.
              if (source !== 'collection') this.hideTab(source);
              return;
            }

            this.deliver(source, panel);
          })
          .catch((e) => {
            console.error(e);
            if (source !== 'collection') this.hideTab(source);
          });
      }

      deliver(source, panel) {
        const button = this.getButton(source);
        const index = button.dataset.index;
        const live = this.panels.querySelector(`.tabs__panel[data-index="${index}"]`);
        const template = this.panels.querySelector(`template[data-index="${index}"]`);

        if (live && !live.hidden) {
          live.replaceWith(document.importNode(panel, true));
        } else {
          if (live) live.remove();
          template.content.replaceChildren(document.importNode(panel, true));
        }

        button.hidden = false;
        this.syncActiveTab();
      }

      // The route fetch asks for one extra product so the current one can be dropped.
      trimCollection(panel) {
        panel.querySelector(`[data-mfy-product="${this.productId}"]`)?.remove();
        const cards = panel.querySelectorAll('[data-mfy-product]');
        for (let i = this.productsToShow; i < cards.length; i++) cards[i].remove();
      }

      hideTab(source) {
        const button = this.getButton(source);
        if (!button) return;
        button.hidden = true;
        this.syncActiveTab();
      }

      // Keep an open, visible tab; hide the whole section when every tab is empty.
      syncActiveTab() {
        const buttons = Array.from(this.querySelectorAll('.tabs__btn'));
        const visible = buttons.filter((button) => !button.hidden);
        this.hidden = visible.length === 0;
        if (!visible.length || typeof this.tabs.setActiveTab !== 'function') return;

        const current = buttons[this.tabs.currentIndex];
        if (current && current.hidden) this.tabs.setActiveTab(Number(visible[0].dataset.index));
      }

      // Mobile: the tab row scrolls sideways. Going forward, the active tab slides to the start so
      // the next one shows; going back, it slides to the end so the previous one shows.
      revealActiveTab(index) {
        const forward = index > this.activeIndex;
        this.activeIndex = index;

        const button = this.querySelector('.tabs__btn.active');
        if (!this.nav || !button || this.nav.scrollWidth <= this.nav.clientWidth) return;

        const buttonRect = button.getBoundingClientRect();
        const navRect = this.nav.getBoundingClientRect();
        const style = getComputedStyle(this.nav);
        const offset = forward
          ? buttonRect.left - navRect.left - (parseFloat(style.paddingInlineStart) || 0)
          : buttonRect.right - navRect.right + (parseFloat(style.paddingInlineEnd) || 0);
        this.nav.scrollTo({ left: this.nav.scrollLeft + offset });
      }

      getButton(source) {
        return this.querySelector(`.tabs__btn[data-mfy-source="${source}"]`);
      }

      getRecentIds() {
        let items = [];
        try {
          items = JSON.parse(window.localStorage.getItem('hypertheme:recently-viewed') || '[]');
        } catch (e) {
          return [];
        }

        return items.filter((id) => id !== this.productId).slice(0, this.productsToShow);
      }

      /**
       * The last crumb the breadcrumb shows. snippets/finnstyle-breadcrumb-trail.liquid
       * stores the trail it displays (fixed or the shopper's click route), followed by
       * this page, so this follows exactly what the shopper sees.
       */
      getRouteCollection() {
        let route = [];
        try {
          route = JSON.parse(sessionStorage.getItem('finnstyle-route')) || [];
        } catch (e) {
          return null;
        }

        const me = route[route.length - 1];
        if (!me || !me.p || me.u !== location.pathname) return null;

        const last = route[route.length - 2];
        return last ? last.h : null;
      }
    }
  );
}
